// src/pages/ProductsList.jsx
import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";
import Loader from "./Loader";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Trash2, Eye } from "lucide-react";
import DeleteModal from "../Sections/DeleteModal";
import toast from "react-hot-toast";
import Switch from "react-switch";

import {
  product,
  server_post_json,
  server_post_data,
  delete_product,
  product_status,
} from "../../ServiceConnection/serviceconnection";

export default function ProductsList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const navigate = useNavigate();
  const categoriesList = [
    "all",
    "combo-tees",
    // "new-arrival",
    "winter-edit-25",
    "oversized-t-shirts",
    "acid-wash-t-shirt",
    "plain-t-shirts",
    "polo-t-shirt",
    "tie-dye-t-shirts",
    "joggers-sweatpants",
  ];
  
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("product_category") || "all";
  });
  /* ===============================
     FETCH PRODUCTS
  =============================== */
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await server_post_json(product);
      setProducts(res.data?.products || []);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  console.log("products", products);

  /* ===============================
     DELETE PRODUCT
  =============================== */

  const handleDeleteProduct = async () => {
    if (!selectedProductId) return;

    try {
      setDeleting(true);

      await server_post_json(delete_product, {
        product_id: selectedProductId,
      });

      toast.success("Product deleted");
      setDeleteModalOpen(false);
      setSelectedProductId(null);
      fetchProducts();
    } catch {
      toast.error("Failed to delete product");
    } finally {
      setDeleting(false);
    }
  };

  /* ===============================
     STATUS TOGGLE (SWITCH)
  =============================== */
  const toggleStatus = async (product_id) => {
    const prevProducts = [...products]; // 🔁 rollback ke liye

    // 🔥 Optimistic UI update
    setProducts((prev) =>
      prev.map((p) =>
        p.product_id === product_id
          ? {
              ...p,
              status: p.status === "Active" ? "Draft" : "Active",
            }
          : p
      )
    );

    try {
      setStatusLoadingId(product_id);

      await server_post_data(product_status, { product_id });

      toast.success("Status updated");
    } catch (err) {
      console.error(err);

      // ❌ error aaya → rollback
      setProducts(prevProducts);
      toast.error("Failed to update status");
    } finally {
      setStatusLoadingId(null);
    }
  };

  /* ===============================
     SKU PREVIEW
  =============================== */
  const getSkuPreview = (p) => {
    const skus = [];
    (p.variants || []).forEach((c) => {
      (c.variants || []).forEach((v) => {
        if (v.code) skus.push(v.code);
      });
    });

    if (!skus.length) return "—";
    return skus.length === 1 ? skus[0] : `${skus[0]} +${skus.length - 1}`;
  };

  /* ===============================
     COLLECTIONS PREVIEW
  =============================== */
  const getCollectionsPreview = (collections = []) => {
    if (!collections.length) return "—";

    return collections.map((c) => c.title).join(", ");
  };

  /* ===============================
     FILTER
  =============================== */
  const filteredProducts = products.filter((p) => {
    const matchSearch = p.title
      ?.toLowerCase()
      .includes(search.toLowerCase());
  
    const matchCategory =
      selectedCategory === "all" ||
      p.collections?.some(
        (c) => c.slug?.toLowerCase() === selectedCategory.toLowerCase()
      );
  
    return matchSearch && matchCategory;
  });

  return (
    <MasterLayout>
      {loading && <Loader />}

      <div className="py-5 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Products</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage inventory, fulfillment & collections
          </p>
        </div>
        <div className="bg-white border rounded-lg mb-6 p-4 flex flex-wrap gap-4 justify-between items-center shadow-sm">

{/* 🔍 Search */}
<input
  type="text"
  placeholder="Search products by title..."
  value={search}
  onChange={(e) => setSearch(e.target.value)}
  className="px-4 py-2 border rounded-md text-sm w-full sm:w-64"
/>

{/* 🔽 Category Dropdown */}
<select
  value={selectedCategory}
  onChange={(e) => {
    setSelectedCategory(e.target.value);
    localStorage.setItem("product_category", e.target.value);
  }}
  className="px-4 py-2 border rounded-md text-sm w-full sm:w-56"
>
  {categoriesList.map((cat) => (
    <option key={cat} value={cat}>
      {cat === "all" ? "All Categories" : cat}
    </option>
  ))}
</select>
<span className="text-sm text-gray-500">
  Total: {filteredProducts.length} 
</span>
{/* ➕ Add Button */}
<Link
  to="/addproducts"
  className="px-6 py-2.5 bg-[var(--primary-color)] text-white rounded-lg text-sm font-medium"
>
  Add Product
</Link>
</div>
   

        {/* Table */}
        <div className="bg-white border rounded-lg overflow-x-auto shadow-md">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Compare Price
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Inventory
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Collections
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filteredProducts.map((p) => (
                <tr key={p.product_id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      {p.mainImages?.[0] ? (
                        <img
                          src={p.mainImages[0]}
                          alt={p.title}
                          className="w-16 h-16 object-cover rounded-md border border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs">
                          No Image
                        </div>
                      )}
                      <span className="font-medium text-sm text-gray-800">
                        {p.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-sm text-gray-800">
                    ₹{p.price}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {p.compareAtPrice ? (
                      <span className="line-through">₹{p.compareAtPrice}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {getSkuPreview(p)}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {p.totalInventory || 0}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    {getCollectionsPreview(p.collections)}
                  </td>

                  {/* 🔥 STATUS TOGGLE */}
                  <td className="px-6 py-4 text-xs">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={p.status === "Active"}
                        onChange={() => toggleStatus(p.product_id)}
                        disabled={statusLoadingId === p.product_id}
                        onColor="#22c55e"
                        offColor="#e5e7eb"
                        checkedIcon={false}
                        uncheckedIcon={false}
                        height={20}
                        width={40}
                      />
                      <span
                        className={`font-medium ${
                          p.status === "Active"
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-right">
                    {/* 👁️ VIEW PRODUCT */}
                    <button
                      onClick={() => navigate(`/product/${p.product_id}`)}
                      className="p-2 hover:bg-blue-50 rounded-md transition"
                      title="View Product"
                    >
                      <Eye size={16} className="text-blue-600" />
                    </button>

                    {/* ✏️ EDIT PRODUCT */}
                    <button
                      onClick={() => navigate(`/editproduct/${p.product_id}`)}
                      className="p-2 hover:bg-gray-100 rounded-md transition"
                      title="Edit Product"
                    >
                      <Pencil size={16} className="text-gray-600" />
                    </button>

                    {/* 🗑️ DELETE PRODUCT */}
                    <button
                      onClick={() => {
                        setSelectedProductId(p.product_id);
                        setDeleteModalOpen(true);
                      }}
                      className="p-2 hover:bg-red-50 rounded-md transition"
                      title="Delete Product"
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && filteredProducts.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center py-8 text-gray-400 text-sm"
                  >
                    No products found. Add a new product to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DELETE MODAL */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        loading={deleting}
        onConfirm={handleDeleteProduct}
        title="Delete product?"
        subtitle="Are you sure you want to delete this product? This action cannot be undone."
      />
    </MasterLayout>
  );
}
